import { Entity } from './common';

/**
 * ─── Capa de wearables (abstracta) ────────────────────────────────────────────
 *
 * Modelo genérico de integración con dispositivos/plataformas de actividad.
 * Diseñado para soportar varios proveedores; `garmin` es el primero implementado
 * (Garmin Health/Wellness API vía OAuth + webhooks). Los tokens OAuth NUNCA se
 * almacenan en estas colecciones legibles por el cliente: viven en
 * `wearable_credentials` (Admin SDK only) — ver Cloud Functions de wearables.
 */

export type WearableProvider = 'garmin' | 'strava' | 'polar';

export const WEARABLE_PROVIDER_LABELS: Record<WearableProvider, string> = {
  garmin: 'Garmin',
  strava: 'Strava',
  polar: 'Polar',
};

export type WearableConnectionStatus =
  | 'PENDING' // OAuth iniciado, sin confirmar
  | 'CONNECTED'
  | 'ERROR'
  | 'REVOKED';

/**
 * Vínculo entre un deportista del tenant y su cuenta de un proveedor.
 * Documento legible por el propio usuario y los gestores — sin secretos.
 * `tenants/{tenantId}/wearable_connections/{connectionId}` (id = `${provider}_${userId}`).
 */
export interface WearableConnection extends Entity {
  userId: string;
  provider: WearableProvider;
  /** Identificador del usuario en el proveedor (no secreto). */
  providerUserId?: string;
  status: WearableConnectionStatus;
  scopes?: string[];
  /** ISO timestamp del último vínculo correcto. */
  connectedAt?: string;
  /** ISO timestamp de la última sincronización de datos recibida. */
  lastSyncAt?: string;
  /** Mensaje de error legible cuando status === 'ERROR'. */
  lastError?: string;
}

/**
 * Métricas diarias agregadas ingeridas desde el proveedor.
 * `tenants/{tenantId}/wearable_daily_metrics/{metricId}` (id = `${userId}_${date}`).
 * Todos los campos son opcionales: cada proveedor envía un subconjunto.
 */
export interface WearableDailyMetrics extends Entity {
  userId: string;
  provider: WearableProvider;
  /** ISO date string 'YYYY-MM-DD'. */
  date: string;
  restingHeartRate?: number; // bpm
  hrv?: number; // ms (variabilidad de la FC)
  sleepMinutes?: number;
  sleepScore?: number; // 0-100
  steps?: number;
  activeCalories?: number;
  vo2Max?: number;
  /** Nivel de estrés medio del proveedor (0-100). */
  stressAvg?: number;
  /** Body Battery / energía (0-100), si el proveedor lo aporta. */
  bodyBattery?: number;
  /** Carga de entrenamiento aguda del día (unidad del proveedor). */
  trainingLoad?: number;
}

export type WearableActivityType =
  | 'RUNNING'
  | 'CYCLING'
  | 'STRENGTH'
  | 'SWIMMING'
  | 'CARDIO'
  | 'OTHER';

/**
 * Actividad/entrenamiento individual importado del proveedor.
 * `tenants/{tenantId}/wearable_activities/{activityId}`.
 */
export interface WearableActivity extends Entity {
  userId: string;
  provider: WearableProvider;
  providerActivityId: string;
  type: WearableActivityType;
  /** ISO timestamp de inicio. */
  startTime: string;
  durationMinutes: number;
  distanceMeters?: number;
  avgHeartRate?: number;
  maxHeartRate?: number;
  calories?: number;
  trainingLoad?: number;
}

// ─── Readiness / carga (deriva de wearable + wellness) ────────────────────────

export type ReadinessLevel = 'GREEN' | 'AMBER' | 'RED';

/**
 * Estado de disponibilidad para entrenar de un deportista en una fecha.
 * Se calcula en cliente cruzando wellness (autoevaluación) y métricas del wearable.
 * No se persiste: es una vista derivada del dashboard de carga/readiness.
 */
export interface ReadinessSnapshot {
  userId: string;
  date: string;
  /** 0-100. */
  score: number;
  level: ReadinessLevel;
  /** Razones legibles que han bajado el score (fatiga, sueño, HRV, carga…). */
  flags: string[];
}

export interface ReadinessInputs {
  /** Media de bienestar 1-5 del día (de TechnificationWellnessEntry). */
  wellnessAvg?: number;
  restingHeartRate?: number;
  /** FC en reposo de referencia (media histórica del deportista). */
  baselineRestingHeartRate?: number;
  hrv?: number;
  baselineHrv?: number;
  sleepMinutes?: number;
  /** Ratio carga aguda:crónica (ACWR). >1.5 = riesgo de sobrecarga. */
  acuteChronicLoadRatio?: number;
}

/**
 * Calcula un snapshot de readiness 0-100 a partir de wellness + métricas wearable.
 * Heurística transparente y conservadora; sirve como alerta, no como diagnóstico.
 */
export function computeReadiness(
  userId: string,
  date: string,
  i: ReadinessInputs,
): ReadinessSnapshot {
  let score = 100;
  const flags: string[] = [];

  if (typeof i.wellnessAvg === 'number') {
    // Bienestar 1-5 → penaliza por debajo de 4
    if (i.wellnessAvg < 4) {
      const penalty = (4 - i.wellnessAvg) * 12;
      score -= penalty;
      flags.push('LOW_WELLNESS');
    }
  }

  if (
    typeof i.restingHeartRate === 'number' &&
    typeof i.baselineRestingHeartRate === 'number' &&
    i.baselineRestingHeartRate > 0
  ) {
    const delta = i.restingHeartRate - i.baselineRestingHeartRate;
    if (delta >= 7) {
      score -= Math.min(20, delta);
      flags.push('ELEVATED_RESTING_HR');
    }
  }

  if (
    typeof i.hrv === 'number' &&
    typeof i.baselineHrv === 'number' &&
    i.baselineHrv > 0
  ) {
    const drop = (i.baselineHrv - i.hrv) / i.baselineHrv;
    if (drop >= 0.15) {
      score -= Math.min(20, drop * 100);
      flags.push('LOW_HRV');
    }
  }

  if (typeof i.sleepMinutes === 'number' && i.sleepMinutes < 360) {
    score -= Math.min(15, (360 - i.sleepMinutes) / 12);
    flags.push('LOW_SLEEP');
  }

  if (
    typeof i.acuteChronicLoadRatio === 'number' &&
    i.acuteChronicLoadRatio > 1.5
  ) {
    score -= Math.min(25, (i.acuteChronicLoadRatio - 1.5) * 40);
    flags.push('HIGH_TRAINING_LOAD');
  }

  score = Math.max(0, Math.round(score));
  const level: ReadinessLevel =
    score >= 75 ? 'GREEN' : score >= 50 ? 'AMBER' : 'RED';

  return { userId, date, score, level, flags };
}
